import React from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { User } from '@spa-cars/models';
import { CURRENT_USER } from '../graphql/queries';
import { Loading } from '../components';

export type TUserContext = {
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User>>;
};

export const UserContext = React.createContext<TUserContext>({
  user: null,
  setUser: () => {},
});

interface UserContextProviderProps {
  children: React.ReactNode;
}

export function UserContextProvider({ children }: UserContextProviderProps) {
  const router = useRouter();

  const [user, setUser] = React.useState<User | null>(null);

  const [loading, setLoading] = React.useState(true);

  const {
    loading: loadingUser,
    data: dataUser,
    error: errorUser,
  } = useQuery<{
    currentUser: { user: User };
  }>(CURRENT_USER);

  React.useEffect(() => {
    if (!loadingUser) {
      if (!!dataUser && dataUser?.currentUser && dataUser?.currentUser?.user) {
        setUser(dataUser.currentUser.user);
      } else if (
        router.pathname.includes('reset-password') ||
        router.pathname.includes('privacy') ||
        router.pathname.includes('terms-and-conditions')
      ) {
        setLoading(false);
        return;
      } else if (errorUser || !dataUser || !dataUser?.currentUser.user) {
        setUser(null);
        router.push('/');
      }
      setLoading(false);
    }
  }, [dataUser, errorUser, loadingUser]);

  const context = React.useMemo(() => ({ user, setUser }), [user, setUser]);
  return (
    <UserContext.Provider value={context}>
      {loading || loadingUser ? <Loading /> : children}
    </UserContext.Provider>
  );
}
